import * as Interface from '@interface/index';
import {observable, action, computed} from 'mobx';
import Router from 'next/router';
import type {JoinedRoomProps} from 'poker-interfaces/game-room/game-room-interface';
import BaseStore from './baseStore';
import {isEqual} from 'lodash-es';
import EventEmitter from 'eventemitter3';

export type MessageType = 'success' | 'error' | 'info' | 'warning';

export type BottomSheetType =
  | 'login'
  | 'profileSettings'
  | 'depositWithdraw'
  | 'setup2FA'
  | 'setup2FAInVerification'
  | 'verifyEmail'
  | 'verifyId'
  | 'disable2FA'
  | 'walletConnectSignMessage'
  | 'purchase'
  | 'luckyDraw'
  | 'tooManyRooms'
  | 'disclaimer';

interface Message {
  type: MessageType;
  content: string;
}

interface LoadingEvent {
  id: number;
  message: string;
}

const areAllPropsEqual = (currentProps: Interface.TabBubble, nextProps: Interface.TabBubble) => {
  for (const [key, value] of Object.entries(nextProps)) {
    if (currentProps?.[key] === undefined) return false;
    if (!isEqual(currentProps[key], value)) return false;
  }

  return true;
};

class SystemStore extends BaseStore {
  @observable message: Message = null;

  @observable loadingList: LoadingEvent[] = [];

  @observable isGameOverlayShown: boolean = false;

  @observable activeTableIndex: number = 0;

  @observable joinedRooms: JoinedRoomProps[] = [];

  @observable tabBubbleContent: Interface.TabBubble[] = [null, null, null, null];

  @observable tablesTransition: string = 'left 1s ease-out';

  @observable hasInitGamePageShown: boolean = false;

  @observable modalText: string = null;

  @observable modalSheetType: BottomSheetType;

  @observable isDebugMode: boolean = false;

  @observable eventEmitter = new EventEmitter();

  @observable tournamentMaxTableReachedData: {
    tournamentId: number;
    tournamentName: string;
  } = {
    tournamentId: null,
    tournamentName: null,
  };

  @observable tournamentCancelData: {
    tournamentName: string;
    refundAmount?: number;
  } = {
    tournamentName: '',
    refundAmount: null,
  };

  constructor() {
    super();
    this.isReady = true;
  }

  @action showGameOverlay() {
    // console.log(' @@ showGameOverlay triggered ');
    const currentQuery = Router.query;
    // ! if the below push is hidden, table position will be affected
    Router.push({
      pathname: '/home',
      query: currentQuery,
    });
    this.isGameOverlayShown = true;
  }

  @action showGameOverlayWithoutPush() {
    this.isGameOverlayShown = true;
  }

  @action hideGameOverlay() {
    this.isGameOverlayShown = false;
  }

  @action showModalSheet(type: BottomSheetType) {
    if (this.modalSheetType !== type) {
      this.modalSheetType = type;
    }
  }

  @action dismissModalSheet() {
    this.modalSheetType = null;
  }

  @action setBubbleContent(index: number, content: Interface.TabBubble) {
    if (!areAllPropsEqual(this.tabBubbleContent[index], content)) {
      this.tabBubbleContent[index] = {...this.tabBubbleContent[index], ...content};
    }
  }

  @action setBubbleAlert(index: number, state: boolean) {
    if (!this.tabBubbleContent[index]) {
      return;
    }
    if (!this.isGameOverlayShown) {
      this.tabBubbleContent[index].hasAlert = state;
    } else if (this.tabBubbleContent[index] && this.activeTableIndex !== index) {
      this.tabBubbleContent[index].hasAlert = state;
    }
  }

  @action setActiveTableIndex(index: number) {
    console.log('index activeTableIndex', index, this.activeTableIndex);
    if (this.activeTableIndex !== index) {
      this.activeTableIndex = index;
    }
  }

  @action goNextTable(joinedRooms: JoinedRoomProps[]): void {
    if (this.activeTableIndex < 3 && joinedRooms[this.activeTableIndex + 1]) {
      this.activeTableIndex += 1;
    }
  }

  @action goPrevTable(joinedRooms: JoinedRoomProps[]): void {
    if (this.activeTableIndex > 0 && joinedRooms[this.activeTableIndex - 1]) {
      this.activeTableIndex -= 1;
    }
  }

  @action showMessage(message) {
    this.message = message;
  }

  @action showModal(message: string) {
    this.modalText = message;
  }

  @action dismissModal() {
    this.modalText = null;
  }

  @action enableTablesTransition() {
    this.tablesTransition = 'left 0.2s ease-out';
  }

  @action disableTablesTransition() {
    this.tablesTransition = null;
  }

  @action clearMessage() {
    this.message = null;
  }

  @computed get isLoading() {
    return this.loadingList.length > 0;
  }

  @action startLoading(message) {
    const id = this.loadingList.length + 1;
    this.loadingList.push({
      id,
      message,
    });
    return id;
  }

  @action stopLoading(id) {
    let selectedEventIndex;
    for (let i = 0; i < this.loadingList.length; i += 1) {
      if (this.loadingList[i].id === id) {
        selectedEventIndex = i;
        break;
      }
    }
    this.loadingList.splice(selectedEventIndex, 1);
  }

  @action setIsDebugMode(isDebugMode: boolean) {
    this.isDebugMode = isDebugMode;
  }

  @action setTournamentMaxTableReachedData(data: {tournamentId: number; tournamentName: string}) {
    this.tournamentMaxTableReachedData = data;
  }

  @action setTournamentCancelData(data: {tournamentName: string; refundAmount?: number}) {
    this.tournamentCancelData = data;
  }

  on(event: string, callback: Function) {
    this.eventEmitter.on(event, callback);
  }

  off(event: string, callback: Function) {
    this.eventEmitter.off(event, callback);
  }

  emitTournamentNotification(tabBubbleRef: React.RefObject<HTMLElement>, roomCode: string) {
    this.eventEmitter.emit('tournamentNotification', {tabBubbleRef, roomCode});
  }

  dismissTournamentNotification(roomCode: string) {
    this.eventEmitter.emit('dismissTournamentNotification', roomCode);
  }

  emitReEntryOnClose(roomCode: string) {
    this.eventEmitter.emit('reEntryOnClose', roomCode);
  }
}

export default SystemStore;

export const systemStore = new SystemStore();
